import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Store, Save, ShieldCheck, Info, Loader2, CheckCircle2, Building2 } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { Panel } from "./ui";
import { Header } from "./CorporateDashboard";

export default function PaymentsConfig() {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [settings, setSettings] = useState(null);
  const [savingRow, setSavingRow] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = () => api.get("/admin/payments/config").then((r) => {
    setData(r.data);
    setSettings(r.data.settings);
    setEdits(Object.fromEntries(r.data.restaurants.map((x) => [x.id, x.razorpay_account_id || ""])));
  });
  useEffect(() => { load(); }, []);

  const saveRow = async (rid) => {
    setSavingRow(rid);
    try {
      await api.patch(`/admin/restaurants/${rid}/payment`, { razorpay_account_id: edits[rid] });
      toast.success("Linked account saved");
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingRow(null); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/payments/settings", settings);
      toast.success("Settlement settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingSettings(false); }
  };

  if (!data || !settings) return <div className="space-y-6"><Header title="Payments & Route" /><Loader2 className="animate-spin text-amber-400" /></div>;

  const linkedCount = data.restaurants.filter((r) => r.razorpay_account_id).length;

  return (
    <div className="space-y-6">
      <Header title="Payments & Route Settlement" sub="Link each restaurant's Razorpay merchant account for real-time split payouts" />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Settlement model */}
        <Panel title="Mall Share & Model" className="lg:col-span-1">
          <div className="space-y-4">
            <ToggleRow label="Razorpay Route enabled" desc="Turn on after Route is activated on your Razorpay account"
              value={settings.route_enabled} onChange={(v) => setSettings((s) => ({ ...s, route_enabled: v }))} testid="route-enabled" />

            <div>
              <div className="text-xs text-slate-400 mb-1.5">Mall commission share</div>
              <select value={settings.mall_share_mode} onChange={(e) => setSettings((s) => ({ ...s, mall_share_mode: e.target.value }))}
                data-testid="mall-share-mode"
                className="w-full bg-[#16336B] border hairline rounded-lg px-3 py-2 text-sm focus:border-amber-500 outline-none">
                <option value="central">Retain in primary account (central collection)</option>
                <option value="linked">Route to a dedicated mall linked account</option>
              </select>
            </div>

            {settings.mall_share_mode === "linked" && (
              <div>
                <div className="text-xs text-slate-400 mb-1.5">Mall linked account id</div>
                <input value={settings.mall_account_id} onChange={(e) => setSettings((s) => ({ ...s, mall_account_id: e.target.value }))}
                  placeholder="acc_XXXXXXXXXXXX" data-testid="mall-account-id"
                  className="w-full bg-[#16336B] border hairline rounded-lg px-3 py-2 text-sm font-mono focus:border-amber-500 outline-none" />
              </div>
            )}

            <ToggleRow label="Hold payouts until served" desc="Keep each restaurant's share on-hold until its sub-order is completed"
              value={settings.on_hold} onChange={(v) => setSettings((s) => ({ ...s, on_hold: v }))} testid="on-hold" />

            <button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings"
              className="gold-btn w-full rounded-xl py-2.5 flex items-center justify-center gap-2 disabled:opacity-60">
              {savingSettings ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save Settings
            </button>
          </div>

          <div className="mt-5 pt-4 border-t hairline">
            <div className="flex items-center gap-1.5 text-xs text-amber-400 mb-2"><Info size={13} /> Deduction structure</div>
            <div className="space-y-1 text-xs text-slate-300">
              <Row k="Mall commission" v={`${(data.rates.commission * 100).toFixed(0)}% of item value`} />
              <Row k="Gateway fee" v={`${(data.rates.gateway * 100).toFixed(0)}% of order`} />
              <Row k="Platform (Fundle) fee" v={`${(data.rates.platform * 100).toFixed(0)}% of item value`} />
              <p className="text-[11px] text-slate-500 pt-1">Restaurant net payable = total − commission − gateway − platform, per the allocation ledger.</p>
            </div>
          </div>
        </Panel>

        {/* Merchant accounts */}
        <Panel title="Restaurant Merchant Accounts" className="lg:col-span-2"
          action={<span className="text-xs text-slate-400 flex items-center gap-1"><CheckCircle2 size={13} className="text-emerald-400" /> {linkedCount}/{data.restaurants.length} linked</span>}>
          <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1" data-testid="merchant-accounts-list">
            {data.restaurants.map((r) => (
              <div key={r.id} className="card-surface-2 rounded-xl p-3 flex items-center gap-3" data-testid={`merchant-row-${r.id}`}>
                <div className="h-9 w-9 rounded-lg gold-gradient flex items-center justify-center shrink-0">
                  <Store className="text-slate-950" size={16} />
                </div>
                <div className="min-w-0 w-40 shrink-0">
                  <div className="text-sm font-semibold truncate">{r.name}</div>
                  <div className="text-[11px] text-slate-400 truncate">{r.group_name} · {r.foodcourt_name}</div>
                </div>
                <input value={edits[r.id] ?? ""} onChange={(e) => setEdits((p) => ({ ...p, [r.id]: e.target.value }))}
                  placeholder="acc_XXXXXXXXXXXX" data-testid={`acc-input-${r.id}`}
                  className="flex-1 bg-[#08183A] border hairline rounded-lg px-3 py-2 text-sm font-mono focus:border-amber-500 outline-none" />
                <button onClick={() => saveRow(r.id)} disabled={savingRow === r.id} data-testid={`save-acc-${r.id}`}
                  className="shrink-0 card-surface rounded-lg px-3 py-2 text-xs font-semibold hover:border-amber-500 transition-colors flex items-center gap-1.5">
                  {savingRow === r.id ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} className="text-amber-400" />} Save
                </button>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="flex items-start gap-2 text-[11px] text-slate-500">
        <ShieldCheck size={14} className="text-emerald-400 shrink-0 mt-0.5" />
        Onboard each brand as a Razorpay Route linked account (KYC) to get its <span className="font-mono text-slate-400">acc_</span> id. Once saved here, payments split automatically at capture — restaurants are credited their net in real time and the mall/platform share is retained or routed per the model above.
      </div>
    </div>
  );
}

const ToggleRow = ({ label, desc, value, onChange, testid }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="text-sm font-medium">{label}</div>
      <div className="text-[11px] text-slate-500">{desc}</div>
    </div>
    <button onClick={() => onChange(!value)} data-testid={testid}
      className={`shrink-0 w-11 h-6 rounded-full transition-colors relative ${value ? "gold-gradient" : "bg-[#16336B]"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
    </button>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between"><span className="text-slate-400">{k}</span><span>{v}</span></div>
);
